import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  Tooltip,
  TextField,
  InputAdornment,
  Fab,
  Badge,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  QrCode as QrCodeIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchSuppliers, addSupplierToStore } from '../../../app/store/slices/supplierSlice';
import { fetchProductBrands, addBrandToStore } from '../../../app/store/slices/productBrandSlice';
import { fetchProductCategories, addCategoryToStore } from '../../../app/store/slices/productCategorySlice';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';

import CategoryPanel from '../components/CategoryPanel';
import ProductGrid from '../components/ProductGrid';
import CartPanel from '../components/CartPanel';
import CheckoutDialog from '../components/CheckoutDialog';
import PreviewDialog from '../components/PreviewDialog';
import CustomerDialog from '../components/CustomerDialog';
import PrintOptionsDialog from '../components/PrintOptionsDialog';
import A4PreviewDialog from '../components/A4PreviewDialog';
import AddProductDialog from '../../product/components/AddProductDialog';
import AddCustomerDialog from '../../customers/components/AddCustomerDialog';
import MultiplePaymentMethodsSection from '../../invoices/components/MultiplePaymentMethodsSection';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import RepairTicketDialog from '../components/RepairTicketDialog'; // ✅ Repair ticket dialog
import { calcTotals, calcVATBreakdown } from '../utils/totalsCalculator'; // ✅ Centralized calculation utility

import { searchProducts, getProductsPaginated, getCategoriesWithProducts, getCategoryProductsPaginated } from '../../product/services/productService';
import { A4InvoiceTemplate } from '../components/A4InvoiceTemplate';
import { generateSaleTicketPreview, generateRepairTicketPreview } from '../utils/ticketPreviewUtils';
import { getVatRates } from '../../companysettings/services/vatService';
import { createTicket, validateTicketData, transformCartToTicketDto } from '../services/ticketDeCaisseService';
import TICKET_TYPE from '../../../constants/ticketTypes';
import { openCashDrawer, openCashDrawerSerial, addDrawerCommandToReceipt, shouldOpenDrawer } from '../utils/cashDrawerUtils';
import { printReceipt } from '../utils/printService';

function generateReceiptNumber() {
  const prefix = 'TKT';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}${random}`;
}

const PosListPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const posContainerRef = useRef(null);

  const { storeInfo } = useStoreInfo();
  const { hasModulePermission, canCreateModule } = useAuth(); // ✅ Get permission functions
  
  // ✅ Check if user has permission to create repair tickets (using POS module permission)
  const canCreateRepairTicket = hasModulePermission('POS', 'CanCreate');
  
  // ✅ Get suppliers, brands, categories, and payment methods from Redux store
  const suppliersFromStore = useSelector((state) => state.suppliers.suppliers) || [];
  const brandsFromStore = useSelector((state) => state.productBrands.brands) || [];
  const categoriesFromStore = useSelector((state) => state.productCategories.categories) || [];
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  // ✅ Sort payment methods alphabetically by name
  const sortedPaymentMethods = useMemo(() => {
    return [...paymentMethodsFromStore].sort((a, b) => {
      const nameA = (a.name || a.label || '').toUpperCase();
      const nameB = (b.name || b.label || '').toUpperCase();
      return nameA.localeCompare(nameB);
    });
  }, [paymentMethodsFromStore]);

  // State Management
  const [categories, setCategories] = useState([]);
  const [rawCategoriesWithProducts, setRawCategoriesWithProducts] = useState([]); // ✅ Store raw API data with products
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryPages, setCategoryPages] = useState({}); // Track current page for each category
  const [categoryProductsCache, setCategoryProductsCache] = useState({}); // Cache products for each category page
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customVATRate, setCustomVATRate] = useState(21);
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);
  const [vatRates, setVatRates] = useState([]);
  
  // Dialog States
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [categoryCollapsed, setCategoryCollapsed] = useState(false);
  const [cartCollapsed, setCartCollapsed] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [repairTicketDialogOpen, setRepairTicketDialogOpen] = useState(false); // ✅ Repair ticket dialog

  // Payment State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // ✅ Will store payment method object { Id, Name }
  const [paidAmount, setPaidAmount] = useState('');
  const [receiptNumber, setReceiptNumber] = useState(generateReceiptNumber());
  const [useMultiplePayments, setUseMultiplePayments] = useState(false); // ✅ Toggle for multi-payment
  const [paymentMethods, setPaymentMethods] = useState([]); // ✅ Array of {id, method, amount} for multi-payment
  const [walkInCustomerName, setWalkInCustomerName] = useState(''); // ✅ Walk-in customer name

  // Fullscreen State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [currentFormat, setCurrentFormat] = useState('receipt');

  const [discount, setDiscount] = useState(null);
  const [notes, setNotes] = useState('*** THANK YOU FOR YOUR VISIT ***'); // ✅ Default notes
  const [notesDialogOpen, setNotesDialogOpen] = useState(false); // ✅ Notes dialog state
  const [serialNumberDialog, setSerialNumberDialog] = useState({ open: false, item: null, serialNumber: "" }); // ✅ Serial number dialog
  const isPaginatingRef = useRef(false); // ✅ Track pagination state to prevent filter conflicts
  const searchAbortRef = useRef(null); // ✅ Track pending search requests to allow cancellation
  const customerFromCheckoutRef = useRef(false); // ✅ Track if CustomerDialog opened from CheckoutDialog
  const customerFromRepairRef = useRef(false); // ✅ Track if CustomerDialog opened from RepairTicketDialog
  
  // ✅ Cash Drawer State
  const [openDrawerOnPrint, setOpenDrawerOnPrint] = useState(true); // ✅ Toggle to open drawer when printing

  // ✅ Checkout Dialog State
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);

  // ✅ Log store info when it changes
  useEffect(() => {
    if (storeInfo) {
    } else {
      console.warn('⚠️ No store info available in Redux store');
    }
  }, [storeInfo]);

  useEffect(() => {
    fetchProducts();
    
    // ✅ Fetch payment methods on mount
    if (paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }

    // Add fullscreen change event listener
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // No default payment method — user must select one explicitly.

  // ✅ NEW: Fetch categories with products using new endpoint
  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // ✅ Use new categories-with-products endpoint
      const response = await getCategoriesWithProducts(10); // Get first 10 products per category
      
      
      // ✅ Handle both camelCase and PascalCase API responses
      const apiCategories = response?.payload?.Categories || response?.payload?.categories;
      
      if (!apiCategories || apiCategories.length === 0) {
        console.warn('⚠️ No categories or products loaded');
        toast.warning(t('no_products_available') || 'No products available');
        setProducts([]);
        setFilteredProducts([]);
        setCategories([]);
        return;
      }

      const categoriesData = apiCategories;
      
      // ✅ Store raw categories with products for RepairTicketDialog
      setRawCategoriesWithProducts(categoriesData);
      
      // ✅ Flatten all products from all categories
      const allProducts = [];
      const categoryPagesInit = {};
      const categoryCache = {};
      
      categoriesData.forEach(category => {
        // ✅ Handle both camelCase and PascalCase for category fields
        const categoryId = category.CategoryId || category.categoryId;
        const categoryProducts = category.Products || category.products || [];
        
        // Initialize page tracking for each category
        categoryPagesInit[categoryId] = 1;
        
        // Cache first page products
        if (!categoryCache[categoryId]) {
          categoryCache[categoryId] = {};
        }
        categoryCache[categoryId][1] = categoryProducts;
        
        // Add products to the all products array
        if (categoryProducts && categoryProducts.length > 0) {
          categoryProducts.forEach(product => {
            allProducts.push(mapProductStructure(product, category));
          });
        }
      });
      
      setCategoryPages(categoryPagesInit);
      setCategoryProductsCache(categoryCache);

      
      setProducts(allProducts);
      setFilteredProducts(allProducts);
      
      // ✅ Build categories array with metadata
      extractCategoriesFromResponse(categoriesData);
      
    } catch (error) {
      console.error('❌ Error fetching categories with products:', error);
      toast.error(t('failed_to_load_products') || 'Failed to load products');
      setProducts([]);
      setFilteredProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };
  
  // ✅ Helper function to map product structure
  const mapProductStructure = (product, category = null) => ({
    Id: product.id || product.Id,
    Name: product.name || product.Name,
    SKU: product.sku || product.SKU || product.barCode || product.BarCode,
    BarCode: product.barCode || product.BarCode,
    Description: product.description || product.Description,
    SalePrice: product.salePrice || product.SalePrice,
    CostPrice: product.costPrice || product.CostPrice,
    MinSalePrice: product.minSalePrice || product.MinSalePrice,
    AveragePrice: product.averagePrice || product.AveragePrice,
    StockQuantity: product.stockQuantity || product.StockQuantity,
    MinStockLevel: product.minStockLevel || product.MinStockLevel,
    IsActive: product.isActive !== undefined ? product.isActive : product.IsActive,
    IsPromotionProduct: product.isPromotionProduct !== undefined ? product.isPromotionProduct : product.IsPromotionProduct,
    PromotionPercentage: product.promotionPercentage || product.PromotionPercentage,
    ProductCategoryId: category ? (category.categoryId || category.CategoryId) : (product.productCategoryId || product.ProductCategoryId || product.CategoryId),
    ProductCategory: category ? {
      CategoryName: category.categoryName || category.CategoryName
    } : (product.productCategory || product.ProductCategory || (product.categoryName ? {
      CategoryName: product.categoryName
    } : null)),
    SupplierId: product.supplierId || product.SupplierId,
    Supplier: product.supplier || product.Supplier || (product.supplierName ? {
      Name: product.supplierName
    } : null),
    ProductBrandId: product.productBrandId || product.ProductBrandId || product.BrandId,
    ProductBrand: product.productBrand || product.ProductBrand || (product.brandName ? {
      BrandName: product.brandName
    } : null),
    ModelNumber: product.modelNumber || product.ModelNumber,
    ImageUrl: product.imageUrl || product.ImageUrl || product.image || product.Image || product.productImage || product.ProductImage,
    SyncToShopify: product.syncToShopify !== undefined ? product.syncToShopify : product.SyncToShopify,
    IsWishlistProduct: product.isWishlistProduct !== undefined ? product.isWishlistProduct : product.IsWishlistProduct,
    CreatedDate: product.createdDate || product.CreatedDate,
    ModifiedDate: product.modifiedDate || product.ModifiedDate,
    Price: product.salePrice || product.SalePrice,
    Stock: product.stockQuantity || product.StockQuantity,
  });

  // ✅ NEW: Extract categories from API response
  const extractCategoriesFromResponse = (categoriesData) => {
    
    // Map API categories to our format (handle both camelCase and PascalCase)
    const mappedCategories = categoriesData.map(cat => ({
      id: cat.CategoryId || cat.categoryId,
      name: cat.CategoryName || cat.categoryName,
      count: cat.TotalProducts || cat.totalProducts,
      hasMoreProducts: cat.HasMoreProducts || cat.hasMoreProducts,
      icon: getCategoryIcon(cat.CategoryName || cat.categoryName),
      currentPage: 1,
      totalPages: Math.ceil((cat.TotalProducts || cat.totalProducts) / 10),
    }));

    setCategories(mappedCategories);
    
    // ✅ Set FIRST category as default
    if (mappedCategories.length > 0) {
      setSelectedCategory(mappedCategories[0]);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'GENERAL': '📦',
      'PC': '💻',
      'CLAVIERS': '⌨️',
      'ECRANS': '🖥️',
      'MEMOIRES': '💾',
      'DISQUE DUR': '💿',
      'SACS/HOUSSES': '💼',
      'ACCESSOIRES': '🔌',
      'ADAPTATEURS': '🔌',
      'RESEAU': '🌐',
      'BOULANGERIE': '🥖',
      'FRUITS': '🍎',
      'ELECTRONICS': '📱',
      'CLOTHING': '👕',
      'FOOD': '🍔',
      'DRINKS': '🥤',
      'BOOKS': '📚',
    };

    const upperCat = (categoryName || '').toUpperCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (upperCat.includes(key)) {
        return icon;
      }
    }
    return '📦';
  };

  // ✅ FIXED: filterProducts uses useCallback with proper deps to avoid stale closure
  const filterProducts = useCallback((categoryOverride, productsOverride) => {
    const activeCategory = categoryOverride !== undefined ? categoryOverride : selectedCategory;
    const activeProducts = productsOverride !== undefined ? productsOverride : products;

    let filtered = [...activeProducts];

    // Apply category filter
    if (activeCategory && activeCategory.id !== 'all') {
      filtered = filtered.filter(p => p.ProductCategoryId === activeCategory.id);
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, products]);

  // ✅ FIXED: Update addToCart with proper rounding
  const addToCart = (product, quantity = 1) => {
    const existingIndex = cart.findIndex(item => item.Id === product.Id);

    // Product price interpretation depends on TVA include toggle.
    const priceInclVAT = product.IsPriceEdited 
      ? product.SalePrice  // This is the edited price from ProductGrid
      : (product.SalePrice || product.Price || 0);
    
    const vatRate = customVATRate !== null ? customVATRate : 21;
    
    // If TVA is included, convert displayed price to base price (excl VAT).
    // If TVA is excluded, keep price as base price and VAT will be added on top.
    const basePrice = isTvaIncluded
      ? priceInclVAT / (1 + vatRate / 100)
      : priceInclVAT;

    // ✅ Check MinSalePrice validation - compare unit price (incl VAT) with minimum price
    const minPrice = product.MinSalePrice || 0;
    if (minPrice > 0 && priceInclVAT < minPrice) {
      toast.error(`Price €${priceInclVAT.toFixed(2)} is below minimum sale price €${minPrice.toFixed(2)} for ${product.Name}`);
      return; // Don't add to cart if below minimum
    }


    if (existingIndex !== -1) {
      // ✅ Product exists in cart - update quantity AND price if it was edited
      const updatedCart = [...cart];
      
      // If the price was edited, update the price for existing item too
      if (product.IsPriceEdited) {
        updatedCart[existingIndex] = {
          ...updatedCart[existingIndex],
          Quantity: updatedCart[existingIndex].Quantity + quantity,
          Price: Number(basePrice.toFixed(4)), // ✅ Store with 4 decimal precision
          PriceInclVAT: Number(priceInclVAT.toFixed(2)),
          VAT: vatRate,
          OriginalPrice: product.OriginalPrice,
          IsPriceEdited: true,
        };
        toast.success(`${product.Name} ${t('quantity_updated') || 'quantity updated'} with new price €${priceInclVAT.toFixed(2)}`);
      } else {
        updatedCart[existingIndex].Quantity += quantity;
        toast.success(`${product.Name} ${t('quantity_updated') || 'quantity updated'}`);
      }
      
      setCart(updatedCart);
    } else {
      // ✅ New product - add to cart with correctly calculated base price
      setCart([
        ...cart,
        {
          ...product,
          Quantity: quantity,
          Price: Number(basePrice.toFixed(4)), // ✅ Store with 4 decimal precision
          PriceInclVAT: Number(priceInclVAT.toFixed(2)),
          VAT: vatRate,
          OriginalPrice: product.IsPriceEdited ? product.OriginalPrice : priceInclVAT,
          IsPriceEdited: product.IsPriceEdited || false,
          SerialNumber: "", // ✅ Initialize serial number field
          _rowKey: `${product.Id}_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, // ✅ Unique key
        }
      ]);
    }
  };

  const updateCartQuantity = (rowKey, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(rowKey);
      return;
    }

    setCart(cart.map(item =>
      (item._rowKey === rowKey || item.Id === rowKey)
        ? { ...item, Quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (rowKey) => {
    setCart(cart.filter(item => item._rowKey !== rowKey && item.Id !== rowKey));
    toast.info(t('item_removed') || 'Item removed');
  };

  // Recalculates all cart item base prices when the TVA-included toggle changes.
  // PriceInclVAT is the "display price" that never changes; Price is the net base stored per item.
  const handleToggleTvaIncluded = (newValue) => {
    setIsTvaIncluded(newValue);
    setCart(prevCart =>
      prevCart.map(item => {
        const displayPrice = item.PriceInclVAT || 0;
        if (!displayPrice) return item;
        const vatRate = item.VAT != null ? item.VAT : (customVATRate || 21);
        const newBasePrice = newValue
          ? displayPrice / (1 + vatRate / 100)   // price shown includes VAT → extract net
          : displayPrice;                          // price shown is net → use directly
        return { ...item, Price: Number(newBasePrice.toFixed(4)) };
      })
    );
  };

  const clearCart = () => {
    setCart([]);
    setCustomer(null);
    setWalkInCustomerName(''); // ✅ Reset walk-in name
    setPaidAmount('');
    setDiscount(null);
    setCustomVATRate(21);
    setIsTvaIncluded(true);
    setNotes('*** THANK YOU FOR YOUR VISIT ***');
    setReceiptNumber(generateReceiptNumber());
    setSelectedPaymentMethod(null); // No default — user must select
    setUseMultiplePayments(false);
    setPaymentMethods([]);
    toast.info(t('cart_cleared') || 'Cart cleared');
  };
  
  // ✅ Serial number handlers
  const handleOpenSerialNumberDialog = (item) => {
    setSerialNumberDialog({ open: true, item, serialNumber: item.SerialNumber || "" });
  };

  const handleCloseSerialNumberDialog = () => {
    setSerialNumberDialog({ open: false, item: null, serialNumber: "" });
  };

  const handleSaveSerialNumber = () => {
    const { item, serialNumber } = serialNumberDialog;
    if (item) {
      setCart(
        cart.map((cartItem) =>
          cartItem._rowKey === item._rowKey
            ? { ...cartItem, SerialNumber: serialNumber }
            : cartItem
        )
      );
      toast.success(t('serial_number_saved') || 'Serial number saved');
    }
    handleCloseSerialNumberDialog();
  };
  
  // Handler for when a product is created from the dialog
  const handleProductCreated = (createdProduct) => {
    if (createdProduct && createdProduct.Id) {
      // Add the newly created product to cart
      addToCart(createdProduct, 1);
      toast.success(t('product_added_to_cart') || 'Product added to cart');
    }
  };

  // Handler for when a customer is created from the dialog
  const handleCustomerCreated = (createdCustomer) => {
    toast.success(t('customer_created_successfully') || 'Customer created successfully!');
    setAddCustomerDialogOpen(false);
    // Automatically set the newly created customer
    setCustomer(createdCustomer);
    // If the flow was triggered from CheckoutDialog, reopen it
    if (customerFromCheckoutRef.current) {
      customerFromCheckoutRef.current = false;
      setCheckoutDialogOpen(true);
    }
    // If the flow was triggered from RepairTicketDialog, reopen it
    if (customerFromRepairRef.current) {
      customerFromRepairRef.current = false;
      setRepairTicketDialogOpen(true);
    }
  };

  // ✅ Load suppliers, brands, and categories only when opening Add Product dialog
  const handleOpenProductDialog = () => {
    // Fetch suppliers, brands, and categories if not already loaded
    if (suppliersFromStore.length === 0) {
      dispatch(fetchSuppliers());
    }
    if (brandsFromStore.length === 0) {
      dispatch(fetchProductBrands());
    }
    if (categoriesFromStore.length === 0) {
      dispatch(fetchProductCategories());
    }
    setProductDialogOpen(true);
  };

  // ✅ Handle repair ticket submission
  const handleRepairTicketSubmit = async (repairTicketData) => {
    try {
      const { 
        services, 
        pickupDate, 
        customer, 
        paymentMethod, 
        paidAmount, 
        notes, 
        customerPhone, 
        templateHtml, 
        isTvaIncluded,
        useMultiplePayments = false,
        paymentMethodsArray = [],
      } = repairTicketData;

      // Convert services to cart format
      const repairCart = services.map((service, index) => {
        const originalPrice = parseFloat(service.Price) || 0;
        const vatRate = service.VAT || 21;
        
        // ✅ When VAT is included, convert to base price; otherwise price is already base price
        const basePrice = isTvaIncluded && originalPrice > 0
          ? originalPrice / (1 + vatRate / 100)
          : originalPrice;
        
        const priceInclVAT = isTvaIncluded 
          ? originalPrice 
          : originalPrice * (1 + vatRate / 100);
        
        return {
          Id: null, // No product ID for repair services
          ServiceName: service.Name,
          Name: service.Name,
          Description: service.Description || '',
          ServiceDescription: service.Description || '',
          Quantity: service.Quantity,
          Price: basePrice, // ✅ Base price (excl VAT)
          PriceInclVAT: priceInclVAT, // ✅ Price incl VAT
          VAT: vatRate,
          _rowKey: `repair-${Date.now()}-${index}`,
        };
      });

      // ✅ Use the receipt number from the dialog (backend may replace with sequential number)
      const { receiptNumber: dialogReceiptNumber } = repairTicketData;

      // Build ticket DTO parameters
      const ticketParams = {
        cart: repairCart,
        customer,
        walkInCustomerName: customer?.Name || customerPhone || '',
        customerPhone: customerPhone || customer?.PhoneNumber || '',
        // ✅ Only set payment method if there's a paid amount (and not using multiple payments)
        selectedPaymentMethod: paidAmount && paidAmount > 0 && !useMultiplePayments ? paymentMethod : null,
        paidAmount: paidAmount && paidAmount > 0 ? paidAmount : null,
        discount: repairTicketData.discount || null,
        customVATRate: repairTicketData.customVATRate || 21,
        receiptNumber: dialogReceiptNumber, // Use generated receipt number from dialog
        notes: notes || '',
        useMultiplePayments: useMultiplePayments || false,
        paymentMethods: paymentMethodsArray || [],
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.REPAIR, // ✅ Repair ticket
        pickupDate: pickupDate, // ✅ Pickup date
        isTvaIncluded: repairTicketData.isTvaIncluded !== undefined ? repairTicketData.isTvaIncluded : true,
        // ✅ Pass pre-calculated totals from RepairTicketDialog
        preCalculatedTotals: {
          subtotal: repairTicketData.subtotal,
          discountAmount: repairTicketData.discountAmount,
          totalVAT: repairTicketData.vatAmount,
          totalIncl: repairTicketData.total,
        },
      };

      // Transform to ticket DTO
      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);

      // Validate
      const validation = validateTicketData(ticketDto);
      if (!validation.isValid) {
        console.error('❌ Repair ticket validation failed:', validation.errors);
        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        throw new Error('Validation failed');
      }

      // Create ticket
      const createdTicket = await createTicket(ticketDto);
      
      // ✅ Extract the ticket number from the response
      const backendTicketNumber = createdTicket?.payload?.TicketNumber || 
                                   createdTicket?.data?.payload?.TicketNumber ||
                                   createdTicket?.TicketNumber ||
                                   'REPAIR';
      
      toast.success(t('repair_ticket_created_success') || `Repair ticket #${backendTicketNumber} created successfully`);

      // Close dialog
      setRepairTicketDialogOpen(false);

      // ✅ Print the ticket using jsPDF approach (same as sale tickets)
      if (templateHtml) {
        try {
          const { jsPDF } = await import('jspdf');
          const html2canvas = (await import('html2canvas')).default;

          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = templateHtml;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.width = '80mm';
          tempDiv.style.background = 'white';
          tempDiv.style.padding = '10px';
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
          });

          document.body.removeChild(tempDiv);

          const imgData = canvas.toDataURL('image/png');
          const pdfHeight = (canvas.height * 80) / canvas.width;
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [80, pdfHeight],
          });
          pdf.addImage(imgData, 'PNG', 0, 0, 80, pdfHeight);

          pdf.autoPrint();
          window.open(pdf.output('bloburl'), '_blank');

          toast.success(t('print_sent') || 'Print job sent successfully');
        } catch (printError) {
          console.error('❌ Print error:', printError);
          // Don't fail the entire operation if printing fails
          toast.warning(t('print_failed') || 'Ticket created but printing failed');
        }
      }

    } catch (error) {
      console.error('❌ Error creating repair ticket:', error);
      toast.error(t('repair_ticket_creation_failed') || 'Failed to create repair ticket');
      throw error; // Re-throw so RepairTicketDialog knows it failed
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchQuery(''); // Clear search when changing category
    // Reset to page 1 — filteredProducts will be refreshed via filterProducts effect
    setCategoryPages(prev => ({ ...prev, [category.id]: 1 }));
    if (isMobile || isTablet) {
      setCategoryDrawerOpen(false);
    }
  };
  
  // ✅ NEW: Handle category pagination
  const handleCategoryPageChange = async (categoryId, direction) => {
    const currentPage = categoryPages[categoryId] || 1;
    const category = categories.find(c => c.id === categoryId);
    
    if (!category) return;
    
    let newPage = currentPage;
    if (direction === 'next' && currentPage < category.totalPages) {
      newPage = currentPage + 1;
    } else if (direction === 'prev' && currentPage > 1) {
      newPage = currentPage - 1;
    } else {
      return; // No change needed
    }
    
    // ✅ Set pagination flag to prevent filter conflicts
    isPaginatingRef.current = true;
    
    // Check if we already have this page cached
    if (categoryProductsCache[categoryId]?.[newPage]) {
      updateCategoryProducts(categoryId, newPage, categoryProductsCache[categoryId][newPage]);
      // ✅ Reset flag after state updates
      setTimeout(() => {
        isPaginatingRef.current = false;
      }, 100);
      return;
    }
    
    // Fetch new page
    try {
      setLoading(true);
      
      const response = await getCategoryProductsPaginated(categoryId, newPage, 10);
      
      // ✅ Handle both camelCase and PascalCase for products field
      const apiProducts = response?.payload?.Products || response?.payload?.products;
      
      if (apiProducts && apiProducts.length > 0) {
        const newProducts = apiProducts.map(p => mapProductStructure(p, category));
        
        // Update cache
        setCategoryProductsCache(prev => ({
          ...prev,
          [categoryId]: {
            ...prev[categoryId],
            [newPage]: newProducts
          }
        }));
        
        updateCategoryProducts(categoryId, newPage, newProducts);
        toast.success(`${t('loaded_page') || 'Loaded page'} ${newPage} ${t('of') || 'of'} ${category.name}`);
      }
    } catch (error) {
      console.error(`❌ Error fetching page ${newPage} for category ${categoryId}:`, error);
      toast.error(t('failed_to_load_page') || 'Failed to load page');
    } finally {
      setLoading(false);
      // ✅ Reset flag after everything completes
      setTimeout(() => {
        isPaginatingRef.current = false;
      }, 100);
    }
  };
  
  // ✅ Helper to update products when changing pages
  const updateCategoryProducts = (categoryId, newPage, newProducts) => {
    // Update page tracker
    setCategoryPages(prev => ({
      ...prev,
      [categoryId]: newPage
    }));
    
    // ✅ Set filteredProducts directly — do NOT touch products array to avoid
    // triggering the filterProducts cascade which would override the paginated view.
    setFilteredProducts(newProducts);
  };

  const handlePreview = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  const handlePrint = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  // ✅ NEW: Handler to manually open cash drawer
  const handleOpenCashDrawer = async () => {
    try {
      
      // For USB printers, use the print driver method
      const success = await openCashDrawer();
      
      if (success) {
        toast.success(t('cash_drawer_opened') || 'Cash drawer opened');
      } else {
        toast.error(t('failed_to_open_drawer') || 'Failed to open cash drawer');
      }
    } catch (error) {
      console.error('❌ Error opening cash drawer:', error);
      toast.error(t('failed_to_open_drawer') || 'Failed to open cash drawer');
    }
  };

  // ✅ Update handleFormatSelection to handle both preview and print correctly
  const handleFormatSelection = async (format, action) => {
    setPrintOptionsOpen(false);
    setCurrentFormat(format);

    // ── Convert to Invoice ──────────────────────────────────────────────────
    if (format === 'invoice') {
      if (cart.length === 0) {
        toast.error(t('please_add_products') || 'Please add at least one product');
        return;
      }

      // Map cart items → invoiceProducts structure expected by AddInvoicePage
      const mappedProducts = cart.map((item, index) => {
        const basePrice = parseFloat(item.Price) || 0;
        const qty = item.Quantity || 1;
        const vatRate = item.VAT != null ? item.VAT : (customVATRate || 21);
        const totalExcl = qty * basePrice;
        const vatAmount = totalExcl * vatRate / 100;
        const totalIncl = totalExcl + vatAmount;
        return {
          Id: item.Id,
          Name: item.Name,
          Quantity: qty,
          Price: basePrice.toFixed(2),
          VAT: vatRate,
          TotalExcl: totalExcl.toFixed(2),
          VATAmount: vatAmount.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
          TotalVAT: vatAmount.toFixed(2),
          TotalInclVAT: totalIncl.toFixed(2),
          SerialNumber: item.SerialNumber || '',
          _rowKey: item._rowKey || Date.now() + index,
        };
      });

      // Map customer → AsyncSelect value shape used by AddInvoicePage
      const mappedCustomer = customer
        ? {
            Id: customer.Id,
            Name: customer.Name || customer.CompanyName,
            CompanyName: customer.CompanyName || customer.Name,
            Email: customer.Email,
            PhoneNumber: customer.PhoneNumber,
            VATNumber: customer.VATNumber,
            Street: customer.Street,
            City: customer.City,
            PostCode: customer.PostCode,
            Country: customer.Country,
            label: customer.CompanyName || customer.Name,
            value: customer.Id,
          }
        : null;

      navigate('/invoices/add', {
        state: {
          fromPos: {
            products: mappedProducts,
            customer: mappedCustomer,
            discount: discount
              ? { type: discount.type, value: discount.value }
              : null,
            vatRate: customVATRate,
            isTvaIncluded,
            paymentMethodId: selectedPaymentMethod?.Id || null,
            paymentMethodName: selectedPaymentMethod?.Name || null,
            notes: notes || '',
          },
        },
      });
      return;
    }

    // ── Receipt / A4 preview & print ────────────────────────────────────────

    // Check if storeInfo is properly loaded
    if (!storeInfo) {
      console.error('ERROR: Store info is null/undefined! Cannot generate receipt.');
      toast.error('Store information not loaded. Please refresh the page.');
      return;
    }

    let templateHtml;

    if (format === 'a4') {
      templateHtml = A4InvoiceTemplate(
        cart,
        customer,
        storeInfo,
        selectedPaymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate,
        notes, // ✅ Pass notes
        useMultiplePayments, // ✅ Pass multi-payment flag
        paymentMethods, // ✅ Pass payment methods array
        sortedPaymentMethods, // ✅ Pass all payment methods for lookup
        walkInCustomerName // ✅ Walk-in customer name
      );
    } else {
      templateHtml = generateSaleTicketPreview({
        cart,
        customer,
        storeInfo,
        receiptNumber,
        paidAmount,
        paymentMethod: selectedPaymentMethod ? { name: selectedPaymentMethod } : null,
        allPaymentMethods: sortedPaymentMethods,
        useMultiplePayments,
        paymentMethodsArray: paymentMethods,
        discount,
        customVATRate,
        notes,
        walkInCustomerName,
        isTvaIncluded,
      });
    }


    if (action === 'preview') {
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
    } else if (action === 'print') {
      await saveAndPrintDocument(templateHtml, format); // ✅ Use new function that saves first
    }
  };

  // ✅ Checkout Dialog finalizer — receives validated form data + pre-built template HTML from the dialog
  const handleCheckoutFinalize = async ({ format, action, localData, templateHtml }) => {
    const {
      paymentMethod,
      paidAmount: localPaid,
      notes: localNotes,
      useMultiple,
      paymentMethodsArray: localPMs,
      walkInCustomerName: localWalkIn,
      customer: localCustomer,
      openDrawerOnPrint: localOpenDrawer,
      calculatedTotals, // ✅ Pre-calculated totals from CheckoutDialog
    } = localData;

    // Sync state so CartPanel stays in sync
    setSelectedPaymentMethod(paymentMethod);
    setPaidAmount(localPaid);
    setNotes(localNotes);
    setUseMultiplePayments(useMultiple);
    setPaymentMethods(localPMs);
    if (localWalkIn !== undefined) setWalkInCustomerName(localWalkIn);
    if (localCustomer !== undefined) setCustomer(localCustomer);
    if (localOpenDrawer !== undefined) setOpenDrawerOnPrint(localOpenDrawer);

    setCheckoutDialogOpen(false);

    if (action === 'preview') {
      setCurrentFormat(format);
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
      return;
    }

    // action === 'print' or 'register_only'
    setCurrentFormat(format);
    try {
      const ticketParams = {
        cart,
        customer,
        walkInCustomerName: localWalkIn ?? walkInCustomerName,
        customerPhone: localCustomer?.PhoneNumber || customer?.PhoneNumber || '',
        selectedPaymentMethod: paymentMethod,
        paidAmount: localPaid,
        discount,
        customVATRate,
        receiptNumber,
        notes: localNotes,
        useMultiplePayments: useMultiple,
        paymentMethods: localPMs,
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.SALE, // ✅ Sale ticket
        isTvaIncluded, // ✅ Pass TVA inclusion flag for correct calculation
        preCalculatedTotals: calculatedTotals, // ✅ Pass pre-calculated totals from dialog
      };
      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);
      if (!validation.isValid) {
        console.error('❌ Ticket validation failed:', validation.errors);
        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }
      try {
        await createTicket(ticketDto);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (err) {
        toast.error(`Failed to save ticket: ${err.message || 'Unknown error'}`);
        return;
      }
      // Clear cart immediately so POS is ready for next sale
      clearCart();
      // Only print if not register_only
      if (action !== 'register_only') {
        printDocumentOnly(templateHtml, format).catch(() => {});
      }
    } catch (error) {
      toast.error(t('print_failed') || 'Failed to complete checkout');
    }
  };

  // ✅ NEW: Function to save ticket and then print (used when clicking print from print options)
  const saveAndPrintDocument = async (templateHtml, format) => {
    try {
      // ✅ STEP 1: Save ticket to database
      
      const ticketParams = {
        cart,
        customer,
        walkInCustomerName, // ✅ Walk-in customer name
        customerPhone: customer?.PhoneNumber || customer?.Phone || '',
        selectedPaymentMethod: selectedPaymentMethod, // ✅ Send complete payment method object {Id, Name}
        paidAmount,
        discount,
        customVATRate,
        receiptNumber,
        notes: notes || '*** THANK YOU FOR YOUR VISIT ***', // ✅ Use user's notes
        useMultiplePayments, // ✅ Multi-payment flag
        paymentMethods, // ✅ Array of payment methods for multi-payment
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.SALE, // ✅ Sale ticket
        isTvaIncluded, // ✅ Pass TVA inclusion flag for correct calculation
        preCalculatedTotals: null, // ✅ No pre-calculated totals when printing from preview
      };

      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);

      if (!validation.isValid) {
        console.error('❌ Ticket validation failed:', validation.errors);
        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      let createdTicket;
      try {
        createdTicket = await createTicket(ticketDto);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (error) {
        console.error('❌ Failed to save ticket:', error);
        toast.error(`Failed to save ticket: ${error.message || 'Unknown error'}`);
        return;
      }

      // ✅ STEP 2: Print the document
      await printDocumentOnly(templateHtml, format);

      // ✅ STEP 3: Clear cart after successful print and save
      clearCart();

    } catch (error) {
      console.error('❌ Save and print error:', error);
      toast.error(t('print_failed') || 'Failed to print and save document');
    }
  };

  // ✅ Print function — silently via QZ Tray, falls back to browser dialog
  const printDocumentOnly = async (templateHtml, format) => {
    try {
      // Use jsPDF approach (same as TicketDetailPage) for consistent printing
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = templateHtml;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = format === 'a4' ? '210mm' : '80mm';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = format === 'a4' ? '20mm' : '10px';
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL('image/png');

      let pdf;
      if (format === 'a4') {
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        const pdfHeight = (canvas.height * 80) / canvas.width;
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, pdfHeight],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, 80, pdfHeight);
      }

      pdf.autoPrint();
      window.open(pdf.output('bloburl'), '_blank');

      toast.success(t('print_sent') || 'Print job sent successfully');

      if (previewOpen) setPreviewOpen(false);
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error(t('print_failed') || 'Failed to print document');
      throw error;
    }
  };

  // ✅ This is called from preview dialog's print button - saves ticket, prints, and clears cart
  const printDocument = async (templateHtml, format) => {
    try {
      // ✅ STEP 1: Save ticket to database
      
      const ticketParams = {
        cart,
        customer,
        walkInCustomerName, // ✅ Walk-in customer name
        customerPhone: customer?.PhoneNumber || customer?.Phone || '',
        selectedPaymentMethod: selectedPaymentMethod,
        paidAmount,
        discount,
        customVATRate,
        receiptNumber,
        notes: notes || '*** THANK YOU FOR YOUR VISIT ***',
        useMultiplePayments, // ✅ Multi-payment flag
        paymentMethods, // ✅ Array of payment methods for multi-payment
        allPaymentMethods: sortedPaymentMethods, // ✅ Pass full payment methods list
        ticketType: TICKET_TYPE.SALE, // ✅ Sale ticket
        isTvaIncluded, // ✅ Pass TVA inclusion flag for correct calculation
      };

      const { dto: ticketDto, amountInChange } = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);

      if (!validation.isValid) {
        console.error('❌ Ticket validation failed:', validation.errors);
        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      let createdTicket;
      try {
        createdTicket = await createTicket(ticketDto);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (error) {
        console.error('❌ Failed to save ticket:', error);
        toast.error(`Failed to save ticket: ${error.message || 'Unknown error'}`);
        return;
      }

      // ✅ STEP 2: Print the document
      await printDocumentOnly(templateHtml, format);

      // ✅ STEP 3: Clear cart after successful print and save
      clearCart();

    } catch (error) {
      console.error('❌ Print from preview error:', error);
      toast.error(t('print_failed') || 'Failed to print and save document');
    }
  };

  // ✅ Updated getReceiptTemplate to use company info from Redux and centralized calculation utility
  const getReceiptTemplate = () => {
    // Use centralized calculation utility for consistency
    const calculatedTotals = calcTotals(cart, discount, null, isTvaIncluded);
    const totalExcl = calculatedTotals.subtotal;
    const totalVAT = calculatedTotals.totalVAT;
    const totalIncl = calculatedTotals.totalIncl;
    const discountAmount = calculatedTotals.discountAmount;
    const change = (parseFloat(paidAmount) || 0) - totalIncl;

    // Calculate VAT breakdown using centralized utility
    const vatBreakdown = calcVATBreakdown(cart, discountAmount, null, isTvaIncluded);

    let vatSectionHtml = '';
    Object.keys(vatBreakdown).forEach((rate) => {
      const { base, vat } = vatBreakdown[rate];
      vatSectionHtml += `
        <tr>
          <td style="padding: 2px 0;">VAT ${rate}%:</td>
          <td style="text-align: right;">€${base.toFixed(2)}</td>
          <td style="text-align: right;">€${vat.toFixed(2)}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            background: white;
            padding: 10px;
          }
          .receipt-container { width: 100%; }
          .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
          .header h1 { font-size: 18px; font-weight: 600; margin-bottom: 5px; }
          .header p { font-size: 10px; margin: 2px 0; }
          .info-section { margin: 10px 0; font-size: 11px; }
          .info-row { display: flex; justify-content: space-between; margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th { text-align: left; border-bottom: 1px solid #000; padding: 5px 0; font-size: 11px; }
          td { padding: 5px 0; font-size: 11px; }
          .item-row td { border-bottom: 1px dotted #ccc; }
          .total-section { margin-top: 15px; border-top: 2px solid #000; padding-top: 10px; }
          .total-row { display: flex; justify-content: space-between; margin: 5px 0; font-weight: 600; }
          .grand-total { font-size: 16px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; border-top: 2px dashed #000; padding-top: 10px; font-size: 10px; }
          .payment-info { margin: 10px 0; padding: 10px; background: #f5f5f5; border: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <h1>TICKET DE CAISSE</h1>
            <p>${storeInfo?.Name || 'Your Store Name'}</p>
            <p>${storeInfo?.Address || '123 Street Address, City'}</p>
            <p>Tel: ${storeInfo?.Phone || storeInfo?.PhoneNumber || '+32 123 456 789'}</p>
            <p>TVA: ${storeInfo?.VATNumber || storeInfo?.TaxNumber || 'BE 0123.456.789'}</p>
            ${storeInfo?.Email ? `<p>Email: ${storeInfo.Email}</p>` : ''}
            ${storeInfo?.Website ? `<p>${storeInfo.Website}</p>` : ''}
          </div>

          <div class="info-section">
            <div class="info-row">
              <span>Receipt #:</span>
              <span><strong>${receiptNumber}</strong></span>
            </div>
            <div class="info-row">
              <span>Date:</span>
              <span>${new Date().toLocaleDateString('fr-BE')} ${new Date().toLocaleTimeString('fr-BE')}</span>
            </div>
            ${customer ? `
              <div class="info-row">
                <span>Client:</span>
                <span><strong>${customer.CompanyName || customer.Name || 'N/A'}</strong></span>
              </div>
            ` : ''}
            <div class="info-row">
              <span>Cashier:</span>
              <span>Admin</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th style="text-align: center;">Qty</th>
                <th style="text-align: right;">Price</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${cart.map(item => `
                <tr class="item-row">
                  <td>${item.Name || 'N/A'}</td>
                  <td style="text-align: center;">${item.Quantity}</td>
                  <td style="text-align: right;">€${item.Price.toFixed(2)}</td>
                  <td style="text-align: right;">€${(item.Quantity * item.Price).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row">
              <span>Subtotal (Excl. VAT):</span>
              <span>€${totalExcl.toFixed(2)}</span>
            </div>
            
            ${discount && discountAmount > 0 ? `
            <div class="total-row" style="color: #d32f2f; font-weight: bold;">
              <span>Discount ${discount.type === 'percentage' ? `(${discount.value}%)` : ''}:</span>
              <span>-€${discountAmount.toFixed(2)}</span>
            </div>
            ` : ''}
            
            <table style="font-size: 10px; margin: 10px 0;">
              <thead>
                <tr>
                  <th>VAT Rate</th>
                  <th style="text-align: right;">Base</th>
                  <th style="text-align: right;">VAT Amount</th>
                </tr>
              </thead>
              <tbody>
                ${vatSectionHtml}
              </tbody>
            </table>

            <div class="total-row">
              <span>Total VAT:</span>
              <span>€${totalVAT.toFixed(2)}</span>
            </div>
            
            <div class="total-row grand-total">
              <span>TOTAL (Incl. VAT):</span>
              <span>€${totalIncl.toFixed(2)}</span>
            </div>
          </div>

          <div class="payment-info">
            ${paidAmount ? `
              <div class="total-row">
                <span>Payment Method:</span>
                <span>${selectedPaymentMethod?.Name || selectedPaymentMethod || 'N/A'}</span>
              </div>
            ` : ''}
            <div class="total-row">
              <span>Paid:</span>
              <span>€${parseFloat(paidAmount || 0).toFixed(2)}</span>
            </div>
            ${paidAmount ? `
              <div class="total-row">
                <span>Change:</span>
                <span>€${change.toFixed(2)}</span>
              </div>
            ` : ''}
          </div>

          <div class="footer">
            <p>${notes || '*** THANK YOU FOR YOUR VISIT ***'}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // ✅ Discount operations
  const handleApplyDiscount = (discountData) => {
    setDiscount(discountData);
    toast.success(
      `${t('discount_applied') || 'Discount applied'}: ${
        discountData.type === 'percentage'
          ? `${discountData.value}%`
          : `€${discountData.value.toFixed(2)}`
      }`
    );
  };

  const handleRemoveDiscount = () => {
    setDiscount(null);
    toast.info(t('discount_removed') || 'Discount removed');
  };

  // ✅ Notes operations
  const handleSaveNotes = (newNotes) => {
    setNotes(newNotes);
    setNotesDialogOpen(false);
    toast.success(t('notes_saved') || 'Notes saved successfully');
  };

  // VAT handler
  const handleApplyVAT = (vatRate) => {
    if (vatRate === null || vatRate === undefined) {
      setCustomVATRate(21); // ✅ Reset to 21% instead of 0
      
      // Update all cart items to 21% VAT and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + 21 / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: 21,
        };
      });
      setCart(updatedCart);
      
      toast.info(t('vat_reset') || 'VAT rate reset to 21%');
    } else {
      setCustomVATRate(vatRate);
      
      // Update VAT for all items and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + vatRate / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: vatRate,
        };
      });
      setCart(updatedCart);
      
      toast.success(t('vat_applied') || `VAT rate changed to ${vatRate}%`);
    }
  };

  // ✅ Enhanced search: local-first, then DB fallback with AbortController to cancel stale calls
  const handleSearch = useCallback(async (searchValue, signal) => {
    if (!searchValue || !searchValue.trim()) {
      setFilteredProducts(products);
      return;
    }

    const query = searchValue.toLowerCase().trim();

    // ✅ STEP 1: Require at least 2 chars to avoid hanging on single-char searches
    if (query.length < 2) {
      setFilteredProducts(products.slice(0, 20)); // Show first 20 products
      return;
    }
    
    // ✅ STEP 2: Search locally, limit to top 20 results
    let localResults = products.filter(p =>
      (p.Name || '').toLowerCase().includes(query) ||
      (p.SKU || '').toLowerCase().includes(query) ||
      (p.BarCode || '').toLowerCase().includes(query) ||
      (p.Description || '').toLowerCase().includes(query) ||
      (p.ProductCategory?.CategoryName || '').toLowerCase().includes(query)
    ).slice(0, 20); // ✅ Limit to top 20 to avoid render hang


    // ✅ If local results found, use them
    if (localResults.length > 0) {
      setFilteredProducts(localResults);
      return;
    }

    // ✅ STEP 3: Check if search query has minimum 4 characters before searching DB
    if (searchValue.length < 4) {
      setFilteredProducts([]); // Show no results for short queries with no local matches
      return;
    }

    // ✅ STEP 4: No local results and query >= 4 chars, search database using pagination
    setLoading(true);

    try {
      const response = await getProductsPaginated({
        pageNumber: 1,
        pageSize: 50, // Limit search results to 50
        searchTerm: searchValue,
        sortBy: 'Name',
        sortDirection: 'Asc'
      });

      // ✅ Abort if a newer search has been initiated
      if (signal?.aborted) return;

      let dbResults = [];
      if (response?.payload?.Items) {
        dbResults = response.payload.Items;
      } else if (response?.payload?.items) {
        dbResults = response.payload.items;
      } else if (response?.Items) {
        dbResults = response.Items;
      } else if (response?.items) {
        dbResults = response.items;
      } else if (response?.payload && Array.isArray(response.payload)) {
        dbResults = response.payload;
      } else if (response?.data && Array.isArray(response.data)) {
        dbResults = response.data;
      } else if (Array.isArray(response)) {
        dbResults = response;
      }

      // ✅ Map database results to match POS structure
      const mappedResults = dbResults.map(product => ({
        Id: product.Id,
        Name: product.Name,
        SKU: product.SKU || product.BarCode,
        BarCode: product.BarCode,
        Description: product.Description,
        SalePrice: product.SalePrice,
        CostPrice: product.CostPrice,
        MinSalePrice: product.MinSalePrice,
        AveragePrice: product.AveragePrice,
        StockQuantity: product.StockQuantity,
        MinStockLevel: product.MinStockLevel,
        IsActive: product.IsActive,
        IsPromotionProduct: product.IsPromotionProduct,
        PromotionPercentage: product.PromotionPercentage,
        ProductCategoryId: product.ProductCategoryId || product.CategoryId,
        ProductCategory: product.ProductCategory || (product.CategoryName ? {
          CategoryName: product.CategoryName
        } : null),
        SupplierId: product.SupplierId,
        Supplier: product.Supplier || (product.SupplierName ? {
          Name: product.SupplierName
        } : null),
        ProductBrandId: product.ProductBrandId || product.BrandId,
        ProductBrand: product.ProductBrand || (product.BrandName ? {
          BrandName: product.BrandName
        } : null),
        ModelNumber: product.ModelNumber,
        ImageUrl: product.ImageUrl,
        SyncToShopify: product.SyncToShopify,
        IsWishlistProduct: product.IsWishlistProduct,
        Price: product.SalePrice,
      }));


      if (mappedResults.length > 0) {
        // ✅ Merge database results with local products (avoid duplicates)
        const existingIds = new Set(products.map(p => p.Id));
        const newProducts = mappedResults.filter(p => !existingIds.has(p.Id));
        
        if (newProducts.length > 0) {
          const updatedProducts = [...products, ...newProducts];
          setProducts(updatedProducts);
        }
        
        setFilteredProducts(mappedResults);
        toast.success(`${t('found') || 'Found'} ${mappedResults.length} ${t('products') || 'product(s)'}`);
      } else {
        // ✅ No results in database either
        setFilteredProducts([]);
        toast.info(`${t('no_products_found_for') || 'No products found for'} "${searchValue}"`);
      }

    } catch (error) {
      if (signal?.aborted) return; // Ignore errors from cancelled requests
      console.error('❌ Database search error:', error);
      toast.error(t('search_failed') || 'Failed to search products');
      setFilteredProducts([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [products]);

  // ✅ Debounced search effect — cancels previous pending API call on each keystroke
  useEffect(() => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery, controller.signal);
      } else {
        filterProducts();
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, handleSearch, filterProducts]);

  // ✅ FIXED: Category/products change — re-filter with fresh values via useCallback
  useEffect(() => {
    if (!searchQuery.trim() && !isPaginatingRef.current) {
      filterProducts();
    }
  }, [filterProducts]); // filterProducts itself changes when selectedCategory or products change

  // ✅ Add toggleFullscreen function after state declarations
  const toggleFullscreen = () => {
    const elem = posContainerRef.current;
    
    if (!document.fullscreenElement && 
        !document.webkitFullscreenElement && 
        !document.mozFullScreenElement && 
        !document.msFullscreenElement) {
      // Enter fullscreen
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const totalCartItems = cart.reduce((sum, item) => sum + item.Quantity, 0);
  const blocker = useUnsavedChanges(cart.length > 0);

  return (
    <Box
      ref={posContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
        bgcolor: '#f5f7fa',
        overflow: 'hidden',
        position: isFullscreen ? 'fixed' : 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isFullscreen ? 9999 : 'auto',
        width: '100%',
      }}
    >
      {/* Header Bar */}
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          zIndex: 1100,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          bgcolor: 'white',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {(isMobile || isTablet) && (
            <IconButton 
              onClick={() => setCategoryDrawerOpen(true)}
              size={isSmallScreen ? "small" : "medium"}
            >
              <MenuIcon />
            </IconButton>
          )}
          {!isFullscreen && (
            <IconButton 
              onClick={() => navigate(-1)}
              size={isSmallScreen ? "small" : "medium"}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {!isSmallScreen && (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('point_of_sale') || 'POS'}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 0.5, sm: 2 } }}>
          <TextField
            size="small"
            placeholder={t('search_products') || 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: 600,
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 0.75, sm: 1 },
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 } }}>
          <Tooltip title={isFullscreen ? t('exit_fullscreen') : t('enter_fullscreen')}>
            <IconButton
              onClick={toggleFullscreen}
              size={isSmallScreen ? "small" : "medium"}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          {!isSmallScreen && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {receiptNumber}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Category Panel */}
        {(isMobile || isTablet) ? (
          <Drawer
            anchor="left"
            open={categoryDrawerOpen}
            onClose={() => setCategoryDrawerOpen(false)}
            container={isFullscreen ? posContainerRef.current : undefined}
            style={{ position: isFullscreen ? 'absolute' : undefined }}
            PaperProps={{
              sx: { width: { xs: 240, sm: 280 } }
            }}
          >
            <CategoryPanel
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelect}
            />
          </Drawer>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexShrink: 0,
              width: categoryCollapsed ? 28 : { xs: 280, sm: 280, md: 260 },
              transition: 'width 0.3s ease',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: { xs: 280, sm: 280, md: 260 }, flexShrink: 0, height: '100%' }}>
              <CategoryPanel
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={handleCategorySelect}
              />
            </Box>
            <Tooltip title={categoryCollapsed ? 'Expand categories' : 'Collapse categories'} placement="right">
              <IconButton
                size="small"
                onClick={() => setCategoryCollapsed(prev => !prev)}
                sx={{
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 24,
                  height: 24,
                  boxShadow: 2,
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
              >
                {categoryCollapsed
                  ? <ChevronRightIcon sx={{ fontSize: 16 }} />
                  : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Product Grid */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* ✅ Pagination Controls - Top Right */}
          {selectedCategory && selectedCategory.totalPages > 1 && (
            <Paper
              elevation={1}
              sx={{
                mx: { xs: 1, sm: 2 },
                mt: { xs: 1, sm: 2 },
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'white',
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {selectedCategory.name}
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({selectedCategory.count} {t('products') || 'products'})
                </Typography>
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {t('page') || 'Page'} {categoryPages[selectedCategory.id] || 1} / {selectedCategory.totalPages}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={t('previous_page') || 'Previous'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={(categoryPages[selectedCategory.id] || 1) <= 1}
                        onClick={() => handleCategoryPageChange(selectedCategory.id, 'prev')}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&:disabled': {
                            bgcolor: 'action.disabledBackground',
                            color: 'action.disabled',
                          },
                        }}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title={t('next_page') || 'Next'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={(categoryPages[selectedCategory.id] || 1) >= selectedCategory.totalPages}
                        onClick={() => handleCategoryPageChange(selectedCategory.id, 'next')}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&:disabled': {
                            bgcolor: 'action.disabledBackground',
                            color: 'action.disabled',
                          },
                        }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          )}
          
          {/* Product Grid */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1, sm: 2 } }}>
          <ProductGrid
            products={filteredProducts}
            loading={loading}
            onSelectProduct={addToCart}
            showCustomProduct={true}
            showRepairTicket={canCreateRepairTicket}
            onCustomProductClick={handleOpenProductDialog}
            onRepairTicketClick={() => setRepairTicketDialogOpen(true)}
            isFullscreen={isFullscreen}
            fullscreenContainer={posContainerRef.current}
            cart={cart}
          />
          </Box>
        </Box>

        {/* Cart Panel */}
        {(isMobile || isTablet) ? (
          <>
            <Zoom in={true}>
              <Fab
                color="primary"
                onClick={() => setCartDrawerOpen(true)}
                sx={{
                  position: 'fixed',
                  bottom: { xs: 16, sm: 24 },
                  right: { xs: 16, sm: 24 },
                  zIndex: 1000,
                }}
              >
                <Badge badgeContent={totalCartItems} color="error" max={99}>
                  <ShoppingCartIcon />
                </Badge>
              </Fab>
            </Zoom>

            <Drawer
              anchor="right"
              open={cartDrawerOpen}
              onClose={() => setCartDrawerOpen(false)}
              container={isFullscreen ? posContainerRef.current : undefined}
              style={{ position: isFullscreen ? 'absolute' : undefined }}
              PaperProps={{
                sx: { width: { xs: '100%', sm: 480 } }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <IconButton onClick={() => setCartDrawerOpen(false)} sx={{ mr: 1 }}>
                  <ChevronRightIcon />
                </IconButton>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {t('cart') || 'Cart'} ({totalCartItems})
                </Typography>
              </Box>
              <CartPanel
                cart={cart}
                customer={customer}
                walkInCustomerName={walkInCustomerName}
                isTvaIncluded={isTvaIncluded}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentMethods={sortedPaymentMethods}
                paidAmount={paidAmount}
                receiptNumber={receiptNumber}
                discount={discount}
                customVATRate={customVATRate}
                vatRates={vatRates}
                notes={notes}
                useMultiplePayments={useMultiplePayments}
                paymentMethodsArray={paymentMethods}
                openDrawerOnPrint={openDrawerOnPrint}
                onUpdateQuantity={updateCartQuantity}
                onRemoveItem={removeFromCart}
                onClearCart={clearCart}
                onSelectCustomer={() => setCustomerDialogOpen(true)}
                onWalkInNameChange={setWalkInCustomerName}
                onPaymentMethodChange={setSelectedPaymentMethod}
                onPaidAmountChange={setPaidAmount}
                onPreview={handlePreview}
                onPrint={handlePrint}
                onApplyDiscount={handleApplyDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onApplyVAT={handleApplyVAT}
                onToggleTvaIncluded={handleToggleTvaIncluded}
                onOpenNotes={() => setNotesDialogOpen(true)}
                onOpenSerialNumber={handleOpenSerialNumberDialog}
                onToggleMultiplePayments={setUseMultiplePayments}
                onPaymentMethodsChange={setPaymentMethods}
                onOpenCashDrawer={handleOpenCashDrawer}
                onToggleDrawerOnPrint={setOpenDrawerOnPrint}
                onOpenCheckout={() => setCheckoutDialogOpen(true)}
              />
            </Drawer>
          </>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexShrink: 0,
              width: cartCollapsed ? 28 : { xs: '100%', md: 460, lg: 480 },
              transition: 'width 0.3s ease',
              overflow: 'hidden',
            }}
          >
            <Tooltip title={cartCollapsed ? 'Expand cart' : 'Collapse cart'} placement="left">
              <IconButton
                size="small"
                onClick={() => setCartCollapsed(prev => !prev)}
                sx={{
                  position: 'absolute',
                  left: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 24,
                  height: 24,
                  boxShadow: 2,
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
              >
                {cartCollapsed
                  ? <ChevronLeftIcon sx={{ fontSize: 16 }} />
                  : <ChevronRightIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
            <Box sx={{ width: { xs: '100%', md: 460, lg: 480 }, flexShrink: 0, height: '100%' }}>
              <CartPanel
                cart={cart}
                customer={customer}
                walkInCustomerName={walkInCustomerName}
                isTvaIncluded={isTvaIncluded}
                selectedPaymentMethod={selectedPaymentMethod}
                paymentMethods={sortedPaymentMethods}
                paidAmount={paidAmount}
                receiptNumber={receiptNumber}
                discount={discount}
                customVATRate={customVATRate}
                vatRates={vatRates}
                notes={notes}
                useMultiplePayments={useMultiplePayments}
                paymentMethodsArray={paymentMethods}
                openDrawerOnPrint={openDrawerOnPrint}
                onUpdateQuantity={updateCartQuantity}
                onRemoveItem={removeFromCart}
                onClearCart={clearCart}
                onSelectCustomer={() => setCustomerDialogOpen(true)}
                onWalkInNameChange={setWalkInCustomerName}
                onPaymentMethodChange={setSelectedPaymentMethod}
                onPaidAmountChange={setPaidAmount}
                onPreview={handlePreview}
                onPrint={handlePrint}
                onApplyDiscount={handleApplyDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onApplyVAT={handleApplyVAT}
                onToggleTvaIncluded={handleToggleTvaIncluded}
                onOpenNotes={() => setNotesDialogOpen(true)}
                onOpenSerialNumber={handleOpenSerialNumberDialog}
                onToggleMultiplePayments={setUseMultiplePayments}
                onPaymentMethodsChange={setPaymentMethods}
                onOpenCashDrawer={handleOpenCashDrawer}
                onToggleDrawerOnPrint={setOpenDrawerOnPrint}
                onOpenCheckout={() => setCheckoutDialogOpen(true)}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Checkout Dialog */}
      <CheckoutDialog
        open={checkoutDialogOpen}
        onClose={() => setCheckoutDialogOpen(false)}
        cart={cart}
        customer={customer}
        walkInCustomerName={walkInCustomerName}
        isTvaIncluded={isTvaIncluded}
        paymentMethods={sortedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        paidAmount={paidAmount}
        notes={notes}
        useMultiplePayments={useMultiplePayments}
        paymentMethodsArray={paymentMethods}
        discount={discount}
        customVATRate={customVATRate}
        receiptNumber={receiptNumber}
        onWalkInNameChange={setWalkInCustomerName}
        onFinalize={handleCheckoutFinalize}
        onConvertToInvoice={() => handleFormatSelection('invoice', null)}
        onCustomerChange={setCustomer}
        onOpenCustomerDialog={() => {
          customerFromCheckoutRef.current = true;
          setCheckoutDialogOpen(false);
          setCustomerDialogOpen(true);
        }}
        vatRates={vatRates}
        openDrawerOnPrint={openDrawerOnPrint}
        onApplyVAT={handleApplyVAT}
        onApplyDiscount={handleApplyDiscount}
        onRemoveDiscount={handleRemoveDiscount}
        container={isFullscreen ? posContainerRef.current : undefined}
      />

      {/* Dialogs */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => setPrintOptionsOpen(false)}
        onSelectFormat={handleFormatSelection}
        container={isFullscreen ? posContainerRef.current : undefined}
        style={{ position: isFullscreen ? 'absolute' : undefined }}
      />

      <CustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onSelectCustomer={(selectedCustomer) => {
          setCustomer(selectedCustomer);
          setCustomerDialogOpen(false);
          if (customerFromCheckoutRef.current) {
            customerFromCheckoutRef.current = false;
            setCheckoutDialogOpen(true);
          }
          if (customerFromRepairRef.current) {
            customerFromRepairRef.current = false;
            setRepairTicketDialogOpen(true);
          }
        }}
        onCreateNewCustomer={() => {
          // keep customerFromCheckoutRef/customerFromRepairRef so handleCustomerCreated can reopen the dialog
          setCustomerDialogOpen(false);
          setAddCustomerDialogOpen(true);
        }}
        currentCustomer={customer}
        container={posContainerRef.current}
        style={{ position: 'absolute' }}
      />

      {currentFormat === 'a4' ? (
        <A4PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />
      ) : (
        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          format="receipt"
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />
      )}

      <AddProductDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        onProductCreated={handleProductCreated}
        suppliersFromStore={suppliersFromStore}
        brandsFromStore={brandsFromStore}
        categoriesFromStore={categoriesFromStore}
        onSupplierCreated={(supplier) => dispatch(addSupplierToStore(supplier))}
        onBrandCreated={(brand) => dispatch(addBrandToStore(brand))}
        onCategoryCreated={(category) => dispatch(addCategoryToStore(category))}
      />

      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onCustomerCreated={handleCustomerCreated}
      />

      {/* ✅ Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        container={isFullscreen ? posContainerRef.current : undefined}
        style={{ position: isFullscreen ? 'absolute' : undefined }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('add_notes') || 'Add Notes'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder={t('enter_notes_placeholder') || 'Enter notes for the receipt (max 300 characters)...'}
            value={notes}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 300) {
                setNotes(value);
              }
            }}
            inputProps={{
              maxLength: 300,
            }}
            helperText={`${notes.length}/300 ${t('characters') || 'characters'}`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNotesDialogOpen(false)} color="inherit">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={() => handleSaveNotes(notes)}
            variant="contained"
            color="primary"
          >
            {t('save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Serial Number Dialog */}
      <Dialog
        open={serialNumberDialog.open}
        onClose={handleCloseSerialNumberDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <QrCodeIcon />
          {t('serialNumber') || 'Serial Number'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {serialNumberDialog.item && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t('product') || 'Product'}:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {serialNumberDialog.item.Name}
              </Typography>
            </Paper>
          )}
          <TextField
            autoFocus
            fullWidth
            label={t('serialNumber') || 'Serial Number'}
            value={serialNumberDialog.serialNumber}
            onChange={(e) =>
              setSerialNumberDialog((prev) => ({
                ...prev,
                serialNumber: e.target.value,
              }))
            }
            placeholder={t('enterSerialNumber') || 'Enter serial number'}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseSerialNumberDialog} color="inherit">
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveSerialNumber}
            variant="contained"
            color="primary"
          >
            {t('save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repair Ticket Dialog */}
      <RepairTicketDialog
        open={repairTicketDialogOpen}
        onClose={() => setRepairTicketDialogOpen(false)}
        onSubmit={handleRepairTicketSubmit}
        paymentMethods={sortedPaymentMethods}
        customer={customer}
        onOpenCustomerDialog={() => {
          // Hide repair dialog and open customer selection
          customerFromRepairRef.current = true;
          setRepairTicketDialogOpen(false);
          setCustomerDialogOpen(true);
        }}
        container={isFullscreen ? posContainerRef.current : undefined}
        storeInfo={storeInfo} // ✅ Pass store info for preview
        categoriesData={rawCategoriesWithProducts} // ✅ Pass categories data to avoid duplicate API call
        categoryProductsCache={categoryProductsCache} // ✅ Pass products cache
      />

      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </Box>
  );
};

export default PosListPage;